import { useState, useCallback, useEffect, useRef } from "react";
import {
	getMessageTemplateById,
	getMessageTemplateUseCases,
	getBrands,
	createMessageTemplate,
	updateMessageTemplate,
} from "../services/messageTemplatesApiService";
import { mapMessageTemplateDetail } from "../mappers/messageTemplateDetailMapper";
import {
	getInitialMessageTemplateFormData,
	MessageType,
} from "../utils/constants";
import { DisplayMessage } from "../../../../../services/helpers";

export const useMessageTemplateData = (
	resources,
	templateId,
	defaultObjectType,
	language,
	accessibleMoboOutlets
) => {
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [redirect, setRedirect] = useState(null);
	const [formData, setFormData] = useState(
		getInitialMessageTemplateFormData(defaultObjectType, language)
	);
	const [errors, setErrors] = useState(() => {
		// In edit mode, don't show errors until data is loaded
		if (templateId) {
			return {
				body: false,
				useCase: false,
				language: false,
			};
		}
		const useCaseError = !formData.useCase?.id || formData.useCase.id === 0;
		const languageError = !formData.language || formData.language.trim() === "";
		const bodyError = !formData.body || formData.body.trim() === "";
		return {
			body: bodyError,
			useCase: useCaseError,
			language: languageError,
		};
	});

	const [brands, setBrands] = useState([]);
	const [useCases, setUseCases] = useState([]);
	const [outlets, setOutlets] = useState([]);
	const [loadingStates, setLoadingStates] = useState({
		objectTypeChange: false,
		messageTypeChange: false,
	});
	const [userChangedFormData, setUserChangedFormData] = useState({
		objectType: false,
		messageType: false,
	});

	const isInitialLoadComplete = useRef(false);

	const [hasViewedPreview, setHasViewedPreview] = useState(true);
	const [showPreviewWarning, setShowPreviewWarning] = useState(false);

	const fetchTemplate = useCallback(
		async (id) => {
			try {
				const data = await getMessageTemplateById(id);
				if (!data) {
					DisplayMessage(resources["MessageTemplateNotFound"], "error");
					setRedirect("/messagetemplates");
					return null;
				}
				return mapMessageTemplateDetail(data);
			} catch (error) {
				DisplayMessage(resources["MessageTemplateNotFound"], "error");
				setRedirect("/messagetemplates");
				return null;
			}
		},
		[resources]
	);

	const fetchBrands = useCallback(async (objectType) => {
		const data = await getBrands(objectType);
		if (data) {
			return data.map((brand) => ({
				id: brand.id,
				description: brand.description,
				objectType: brand.objectType,
			}));
		}
		return [];
	}, []);

	const fetchUseCases = useCallback(async (objectType, messageType) => {
		const data = await getMessageTemplateUseCases(objectType, messageType);
		return data || [];
	}, []);

	const filterOutlets = useCallback((objectType, allOutlets) => {
		if (!allOutlets) return [];
		
		return allOutlets.filter(outlet => outlet.objectType === objectType);
	}, []);

	useEffect(() => {
		if (isInitialLoadComplete.current) {
			return;
		}

		const initializeComponent = async () => {
			setLoading(true);

			try {
				if (templateId) {
					const template = await fetchTemplate(templateId);
					if (!template) {
						return;
					}

					const objectType = template.objectType;
					const messageType = template.messageType;

					setFormData(template);

					setErrors({
						body: !template.body || template.body.trim() === "",
						useCase: !template.useCase?.id || template.useCase.id === 0,
						language: !template.language || template.language.trim() === "",
					});

				const [brandsData, useCasesData] = await Promise.all([
					fetchBrands(objectType),
					fetchUseCases(objectType, messageType),
				]);

				setBrands(brandsData);
				setUseCases(useCasesData);
				setOutlets(filterOutlets(objectType, accessibleMoboOutlets));
			} else {
					// For create mode, use the initial values from state
					const objectType = defaultObjectType || 1; // car
					const messageType = MessageType.email;

				const [brandsData, useCasesData] = await Promise.all([
					fetchBrands(objectType),
					fetchUseCases(objectType, messageType),
				]);

				setBrands(brandsData);
				setUseCases(useCasesData);
				setOutlets(filterOutlets(objectType, accessibleMoboOutlets));
			}

			isInitialLoadComplete.current = true;
			} finally {
				setLoading(false);
			}
		};

		initializeComponent();
	}, [
		templateId,
		fetchTemplate,
		fetchBrands,
		fetchUseCases,
		filterOutlets,
		defaultObjectType,
		resources,
		accessibleMoboOutlets,
	]);

	useEffect(() => {
		if (!userChangedFormData.objectType) {
			return;
		}

		const updateObjectTypeRelatedData = async () => {
			setLoadingStates((prev) => ({
				...prev,
				objectTypeChange: true,
			}));

			try {
			const [brandsData, useCasesData] = await Promise.all([
				fetchBrands(formData.objectType),
				fetchUseCases(formData.objectType, formData.messageType),
			]);

			setBrands(brandsData);
			setUseCases(useCasesData);
			setOutlets(filterOutlets(formData.objectType, accessibleMoboOutlets));
		} finally {
				setLoadingStates((prev) => ({
					...prev,
					objectTypeChange: false,
				}));
				setUserChangedFormData((prev) => ({
					...prev,
					objectType: false,
				}));
			}
		};

		updateObjectTypeRelatedData();
	}, [
		formData.objectType,
		formData.messageType,
		userChangedFormData.objectType,
		fetchBrands,
		fetchUseCases,
		filterOutlets,
		accessibleMoboOutlets,
	]);

	useEffect(() => {
		if (!userChangedFormData.messageType) return;

		const updateMessageTypeRelatedData = async () => {
			setLoadingStates((prev) => ({
				...prev,
				messageTypeChange: true,
			}));

			try {
				const useCasesData = await fetchUseCases(
					formData.objectType,
					formData.messageType
				);

				setUseCases(useCasesData);
			} finally {
				setLoadingStates((prev) => ({
					...prev,
					messageTypeChange: false,
				}));
				setUserChangedFormData((prev) => ({
					...prev,
					messageType: false,
				}));
			}
		};

		updateMessageTypeRelatedData();
	}, [
		formData.objectType,
		formData.messageType,
		userChangedFormData.messageType,
		fetchUseCases,
	]);

	const handleObjectTypeChange = (e) => {
		const newObjectType = parseInt(e.target.value);

		setFormData((prev) => ({
			...prev,
			objectType: newObjectType,
			useCase: {
				id: 0,
				description: "",
			},
			brand: {
				id: 0,
				description: "",
				objectType: newObjectType,
			},
			outletId: 0,
		}));

		setUserChangedFormData((prev) => ({
			...prev,
			objectType: true,
		}));
	};

	const handleMessageTypeChange = (e) => {
		const newMessageType = parseInt(e.target.value);

		setFormData((prev) => ({
			...prev,
			messageType: newMessageType,
			useCase: {
				id: 0,
				useCase: 0,
				description: "",
			},
		}));

		setUserChangedFormData((prev) => ({
			...prev,
			messageType: true,
		}));
	};

	const handleUseCaseChange = (e) => {
		const useCaseId = parseInt(e.target.value);
		const selectedUseCase = useCases.find((uc) => uc.id === useCaseId);

		setFormData((prev) => ({
			...prev,
			useCase: selectedUseCase
				? {
						id: selectedUseCase.id,
						useCase: selectedUseCase.useCase,
						description: selectedUseCase.description,
				  }
				: {
						id: 0,
						useCase: 0,
						description: "",
				  },
		}));

		setErrors((prev) => ({
			...prev,
			useCase: useCaseId <= 0,
		}));
	};

	const handleBrandChange = (e) => {
		const brandId = parseInt(e.target.value);
		const selectedBrand = brands.find((b) => b.id === brandId);

		setFormData((prev) => ({
			...prev,
			brand: selectedBrand
				? {
						id: selectedBrand.id,
						description: selectedBrand.description,
						objectType: selectedBrand.objectType,
				  }
				: {
						id: 0,
						description: "",
						objectType: 1,
				  },
		}));
	};

	const handleIsWaitsOnPremiseDependentChange = (e) => {
		const newValue = e.target.checked;

		setFormData((prev) => ({
			...prev,
			isWaitsOnPremiseDependent: newValue,
			isWaitsOnPremise: newValue ? prev.isWaitsOnPremise : false,
		}));
	};

	const handleIsWaitsOnPremiseChange = (e) => {
		const newValue = e.target.checked;

		setFormData((prev) => ({
			...prev,
			isWaitsOnPremise: newValue,
		}));
	};

	const handleBodyChange = (newBody) => {
		if (formData.messageType === MessageType.email) {
			setHasViewedPreview(false);
		}

		setFormData((prev) => ({
			...prev,
			body: newBody,
		}));

		setErrors((prev) => ({
			...prev,
			body: !newBody || newBody.trim() === "",
		}));
	};

	const handleLanguageChange = (e) => {
		const newLanguage = e.target.value;

		setFormData((prev) => ({
			...prev,
			language: newLanguage,
		}));

		setErrors((prev) => ({
			...prev,
			language: !newLanguage || newLanguage.trim() === "",
		}));
	};

	const markPreviewAsViewed = () => {
		setHasViewedPreview(true);
	};

	const saveMessageTemplate = async (skipPreviewWarningCheck = false) => {
		if (
			formData.messageType === MessageType.email &&
			!hasViewedPreview &&
			formData.body &&
			!skipPreviewWarningCheck
		) {
			setShowPreviewWarning(true);
			return { success: false, previewRequired: true };
		}

		setSaving(true);

		try {
			if (templateId) {
				const result = await updateMessageTemplate(templateId, formData);

				if (result.success) {
					const updatedTemplate = mapMessageTemplateDetail(result.data);

					if (updatedTemplate) {
						setFormData(updatedTemplate);
					}

					DisplayMessage(
						resources["MessageTemplateWasUpdatedWithSuccess"],
						"success"
					);
				}

				return result;
			} else {
				const result = await createMessageTemplate(formData);
				if (result.success) {
					DisplayMessage(
						resources["MessageTemplateWasCreatedWithSuccess"],
						"success"
					);
				}

				return result;
			}
		} catch (error) {
			console.error("Error saving message template:", error);
			return { success: false, error: error.message };
		} finally {
			setSaving(false);
		}
	};

	const handleContinueSaveWithoutPreview = async () => {
		setShowPreviewWarning(false);
		setHasViewedPreview(true);

		return saveMessageTemplate(true);
	};

	const handleOutletChange = (e) => {
		const outletId = parseInt(e.target.value);
		setFormData((prev) => ({
			...prev,
			outletId: outletId,
		}));
	};

	return {
		loading,
		saving,
		redirect,
		setRedirect,
		formData,
		brands,
		useCases,
		outlets,
		loadingStates,
		saveMessageTemplate,
		handleObjectTypeChange,
		handleMessageTypeChange,
		handleUseCaseChange,
		handleBrandChange,
		handleIsWaitsOnPremiseDependentChange,
		handleIsWaitsOnPremiseChange,
		handleBodyChange,
		handleLanguageChange,
		handleOutletChange,
		hasViewedPreview,
		markPreviewAsViewed,
		showPreviewWarning,
		setShowPreviewWarning,
		handleContinueSaveWithoutPreview,
		errors,
	};
};
